<template>
  <RfDialog
    ref="newGuideDialogRef"
    variant="outlined"
    closeOnClickOutside
    width="1220"
    height="762"
    confirmText="Create"
    :disabled="isConfirmDisabled"
    title="New Guide"
    @confirm="handleCreate"
    @close="resetState()"
  >
    <div class="flex h-full min-w-px overflow-hidden">
      <div class="w-full overflow-auto p-8">
        <div class="flex flex-col gap-4">
          <template v-if="appsStore.currAppGuideTemplates.loading">
            <h3 class="text-heading-2 pb-2 pl-2 capitalize">Templates</h3>
            <div class="grid grid-cols-3 gap-2">
              <div
                v-for="i in 3"
                :key="i"
                class="border-beige-10 flex flex-col gap-2 rounded-lg border border-solid p-4"
              >
                <RfBaseSkeleton :height="14" :radius="4" class="w-3/4" />
                <RfBaseSkeleton :height="10" :radius="4" class="w-1/2" />
              </div>
            </div>
          </template>
          <RfCardList
            v-model="guideType"
            :items="guideList"
            titleKey="label"
            descriptionKey="description"
            tagsKey="tags"
            @update:modelValue="
              v =>
                resetState({
                  newGuide: true,
                  engagementCategory: v?.tags?.[0]?.value,
                  engagementSubcategory: v?.engagement_subcategory,
                  isTemplate: v?.isTemplate,
                })
            "
          />
        </div>
      </div>
      <Transition
        enterActiveClass="transition-transform duration-300 ease-out"
        enterFromClass="translate-x-full"
        enterToClass="translate-x-0"
        leaveActiveClass="transition-transform duration-300 ease-in"
        leaveFromClass="translate-x-0"
        leaveToClass="translate-x-full"
      >
        <div v-if="guideType" class="flex h-full">
          <div class="bg-beige-10 w-px"></div>
          <div class="bg-beige-0 flex w-72 shrink-0 grow-0 flex-col gap-4 p-8">
            <div>
              <h3 class="text-heading-2 truncate">{{ guideType.label }}</h3>
              <p v-if="newGuide.isTemplate" class="text-body text-beige-50">
                {{ ENGAGEMENT_CATEGORIES[newGuide.engagementCategory]?.label }}
              </p>
            </div>
            <div class="flex flex-col gap-4">
              <RfInput
                v-model="newGuide.name"
                title="Name"
                placeholder="Enter a name"
                requiredTitle
                variant="outlined"
                autoWidth
                class="flex-1"
              />
              <RfInput
                v-model="newGuide.description"
                title="Description"
                placeholder="Details about this prompt"
                variant="outlined"
                autoWidth
                class="flex-1"
              />
              <RfSelect
                v-if="showEngagementCategoryField && !newGuide.isTemplate"
                v-model="newGuide.engagementCategory"
                title="Engagement Category"
                placeholder="Select an engagement category"
                preset="modalMenu"
                :items="engagementCategories"
                variant="outlined"
                :itemDisabled="item => item.disabled"
                :itemDisabledMessage="item => item.disabledMessage"
                sameWidth
                autoWidth
                requiredTitle
                itemValue="label"
                class="flex-1"
              >
                <template #selected="{ props: tag }">
                  <RfColorTag
                    :title="tag.label"
                    pill
                    disabled
                    :backgroundColor="getCategoryColor(tag.value)"
                  />
                </template>
                <template #item="{ props: tag }">
                  <div class="flex flex-col items-start">
                    <RfColorTag
                      :title="tag.label"
                      pill
                      disabled
                      :backgroundColor="getCategoryColor(tag.value)"
                    />
                    <span class="text-grey-2 ml-2 text-sm">{{ tag.description }}</span>
                  </div>
                </template>
              </RfSelect>
              <RfRadioGroup
                v-if="showRetentionTypeField"
                v-model="retentionTypeRadio"
                legend="Retention type"
                requiredTitle
                vertical
                :options="retentionTypeOptions"
                class="[&_.rf-radio-group--wrapper]:gap-y-4"
              />
              <div v-if="showRetentionTypeField" class="text-caption text-grey-2 mt-1">
                Optional — select "None" if this guide has no retention type.
              </div>
              <RfSegmentSelect v-model="newGuide.segments" requiredTitle />
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </RfDialog>
</template>

<script setup>
import RfDialog from "@/components/modals/RfDialog.vue";
import RfInput from "@/components/inputs/RfInput.vue";
import RfSelect from "@/components/inputs/RfSelect.vue";
import RfRadioGroup from "@/components/inputs/RfRadioGroup.vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import {
  ENGAGEMENT_CATEGORIES,
  getCategoryColor,
} from "@/utils/constants/EngagementCategoryConstants";
import { computed, ref, useTemplateRef, watch } from "vue";
import { GUIDE_TYPES } from "@/utils/constants/GuidesConstants";
import RfCardList from "@/blocks/RfLists/RfCardList.vue";
import RfSegmentSelect from "@/blocks/RfSegmentSelect.vue";
import { useAppsStore } from "@/pinia/appsStore";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import { useEngagementCategories } from "@/utils/composables/useEngagementCategories";
import { useEngagementSubcategories } from "@/utils/composables/useEngagementSubcategories";

const GUIDE_SECTION_TITLES = { templates: "Templates", custom: "Custom Guides" };

const emit = defineEmits(["created"]);
const newGuideDialogRef = useTemplateRef("newGuideDialogRef");
const appsStore = useAppsStore();
const guideType = ref(null);

const {
  engagementCategoriesEnabled: showEngagementCategoryField,
  engagementCategoriesWithRestrictions: engagementCategories,
} = useEngagementCategories();
const { isSubcategoryAllowedFor, radioOptionsForCategory } = useEngagementSubcategories();

const newGuide = ref({
  name: "",
  description: "",
  engagementCategory: Object.values(ENGAGEMENT_CATEGORIES)[0].value,
  engagementSubcategory: undefined,
  segments: [],
});

const showRetentionTypeField = computed(
  () =>
    showEngagementCategoryField.value &&
    !newGuide.value.isTemplate &&
    newGuide.value.engagementCategory === "retention",
);

// "none" is the radio sentinel (mirrors RfBulkEditModal); null is the emitted
// no-subcategory value. undefined means no choice made yet, which the Create
// gate requires picking one of the three options. radioOptionsForCategory is
// the same options source the bulk edit uses, so descriptions render identically.
const retentionTypeOptions = computed(() => radioOptionsForCategory("retention"));

const retentionTypeRadio = computed({
  get: () =>
    newGuide.value.engagementSubcategory === undefined
      ? undefined
      : (newGuide.value.engagementSubcategory ?? "none"),
  set: value => {
    newGuide.value.engagementSubcategory = value === "none" ? null : value;
  },
});

const guideList = computed(() => {
  const res = {};
  const apiTemplates = appsStore.currAppGuideTemplates.value;
  if (Array.isArray(apiTemplates) && apiTemplates.length) {
    res.templates = apiTemplates.map(t => {
      return {
        label: (t.name || "").replaceAll("DEFAULT - ", "").trim(),
        value: t.key,
        description: t.description,
        tags: [ENGAGEMENT_CATEGORIES[t.engagement_category]].filter(Boolean),
        engagement_subcategory: t.engagement_subcategory,
        isTemplate: true,
      };
    });
  }

  res.custom = Object.values(GUIDE_TYPES).filter(
    t => t.value !== GUIDE_TYPES.tour.value || appsStore.app?.flags?.tours,
  );

  return Object.fromEntries(
    Object.entries(res).map(([key, value]) => [GUIDE_SECTION_TITLES[key] ?? key, value]),
  );
});

const isConfirmDisabled = computed(() => {
  if (!newGuide.value.name) return true;
  if (!newGuide.value.segments.length) return true;
  // undefined = no pick yet; null = "None" picked, which counts as a choice
  if (showRetentionTypeField.value && newGuide.value.engagementSubcategory === undefined)
    return true;
  return false;
});

watch(
  () => newGuide.value.engagementCategory,
  category => {
    if (category !== "retention") newGuide.value.engagementSubcategory = undefined;
  },
);

const handleCreate = () => {
  const { name, description, engagementCategory, engagementSubcategory, segments } = newGuide.value;
  emit("created", {
    template_key: newGuide.value.isTemplate ? guideType.value.value : null,
    name,
    description,
    sequence_type: guideType.value.value,
    segments,
    engagement_category: engagementCategory || null,
    engagement_subcategory: engagementCategory === "retention" ? engagementSubcategory : null,
  });
  resetState();
};

const resetState = options => {
  if (!options || options.guideType) guideType.value = null;
  if (!options || options.newGuide) {
    const engagementCategory =
      options?.engagementCategory ?? Object.values(ENGAGEMENT_CATEGORIES)[0].value;
    newGuide.value = {
      name: "",
      description: "",
      engagementCategory,
      // Template cards carry their engagement_subcategory; seed it only when the
      // category allows that value. Custom guides start unpicked (undefined) so
      // the retention type stays required.
      engagementSubcategory: options?.isTemplate
        ? isSubcategoryAllowedFor(engagementCategory, options?.engagementSubcategory)
          ? (options?.engagementSubcategory ?? null)
          : null
        : undefined,
      segments: [],
      isTemplate: options?.isTemplate || false,
    };
  }
};

defineExpose({
  open: () => newGuideDialogRef.value?.open(),
  close: () => newGuideDialogRef.value?.close(),
});
</script>
